// Case — chapter / unit / topic anchor (the spine of an IngestPackage).
//
// "Case" is the historical IB term; UI displays it as "Case" / "Chapter" /
// "Unit" / "Topic" depending on pedagogy (caseLabel field on CourseDraft).
//
// Decision history:
//   Session 19A: created with title_en + title_zh + name (3 alt naming
//     fields) for v1 IB / Brand back-compat.
//   Session 19AE: collapsed to single `title` field (breaking). v1 IB courses
//     are archived. `subSection.label` → `title` for naming consistency.
//   Session 19AP: actually wired the forward migration (the 19AE comment had
//     claimed it was done, but the function never existed) — see
//     `migrateLegacyIngestPackage` in ./migrate-legacy.ts. Paste-JSON path
//     and any future legacy load paths must call it before safeParse.
import { z } from 'zod';
const SubSectionSchema = z.object({
    id: z.string().min(1)
        .describe('Sub-section slug (snake_case).'),
    title: z.string().min(1)
        .describe('Sub-section title — short label shown in UI section pills.'),
});
export const CaseSchema = z.object({
    id: z.string().regex(/^[a-z][a-z0-9_]*$/, 'caseId must be snake_case')
        .describe('Snake_case slug. Used as caseId on flashcards / mcqs / etc. Lowercase only.'),
    order: z.number().int().positive()
        .describe('1-indexed order this case appears in the dashboard / sidebar.'),
    title: z.string().min(1).max(120)
        .describe('Concise case title (≤120 chars). Per contentLanguage mode: en/en_zh_gloss → English; zh/zh_en_terms → Chinese (with English term retained inline if zh_en_terms). 19BG+2 Day 6: bumped from 80 to 120 — IB Tier 3 verbatim PDF chapter titles run up to 86 chars (e.g. "Shaping the Future of Solar Power Climate Change, Industrial Policy and Free Trade"); 80 was rejecting valid titles. UI components MUST truncate at display time, not rely on schema floor.'),
    subtitle: z.string().min(15)
        .describe('One-sentence summary of what this case covers. ≥15 chars (substantive · not "Chapter 1"). Shown under title in sidebar / dashboard cards. Constraint Session 19AO.'),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'color must be #RRGGBB')
        .describe('Hex color code (#RRGGBB) — distinct per case for visual distinction. Suggested palette: #1e3a5f / #b45309 / #2d6a4f / #6d2e46 / #5b21b6 / #b91c1c / #0e7490.'),
    keyFrameworks: z.array(z.string()).default([])
        .describe('Named frameworks / models / theories central to this case (e.g. "OCA theory", "Master Theorem", "CBBE pyramid"). Drives glossary cross-link.'),
    keyPlayers: z.array(z.string()).default([])
        .describe('People / orgs / countries named in the case (e.g. "Argentina 2001", "Dow Chemical", "European Commission"). Optional for non-historical cases.'),
    keyYears: z.array(z.number().int()).default([])
        .describe('Significant year markers. Optional — empty for case studies without strong temporal anchors.'),
    hasSubSections: z.boolean()
        .describe('True if this case is split into named sub-sections (e.g. Rare Earth + NAFTA combined). Drives section pill UI.'),
    subSections: z.array(SubSectionSchema).nullable()
        .describe('When hasSubSections=true: array of sub-section objects. When false: null. Validated by refine.'),
}).passthrough().refine(
// LLM_NATIVE_ARCHITECTURE §2.4: passthrough preserves unknown LLM-emitted fields.
// .passthrough() must come BEFORE .refine() — refine returns a ZodEffects which
// doesn't have .passthrough().
(c) => (c.hasSubSections ? c.subSections !== null && c.subSections.length > 0 : c.subSections === null), { message: 'hasSubSections=true requires non-empty subSections; false requires null' });
export const CasesSchema = z.array(CaseSchema);
//# sourceMappingURL=case.js.map